import React from 'react';
import { ArrowLeft, FileText, Scale, ShoppingCart, Shield, AlertTriangle, Globe, Users, Clock, Gavel } from 'lucide-react';

interface TermsConditionsProps {
  onBack: () => void;
}

export default function TermsConditions({ onBack }: TermsConditionsProps) {
  return (
    <>
      <style jsx>{`
        .font-inter { font-family: 'Inter', sans-serif; }
        .font-poppins { font-family: 'Poppins', sans-serif; }
        
        .glass-morphism {
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.12);
        }
        
        .premium-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
          backdrop-filter: blur(30px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        
        .gradient-text {
          background: linear-gradient(135deg, #ec4899, #8b5cf6, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 relative overflow-hidden">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="fixed top-6 left-6 z-50 glass-morphism text-pink-400 px-6 py-3 rounded-full font-medium hover:bg-pink-500/10 transition-all duration-300 flex items-center space-x-2"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back</span>
        </button>

        <div className="pt-32 pb-20 relative">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Scale className="h-8 w-8 text-white" />
              </div>
              <h1 className="font-poppins font-black text-4xl lg:text-6xl text-white mb-4">
                Terms & Conditions
              </h1>
              <p className="font-inter text-xl text-slate-300">
                Effective Date: January 1, 2025
              </p>
            </div>

            {/* Content */}
            <div className="premium-card rounded-3xl p-8 lg:p-12">
              <div className="prose prose-lg prose-invert max-w-none">
                <div className="space-y-8">
                  {/* Introduction */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4 flex items-center space-x-3">
                      <FileText className="h-6 w-6 text-cyan-400" />
                      <span>Agreement to Terms</span>
                    </h2>
                    <p className="font-inter text-slate-300 leading-relaxed">
                      These Terms and Conditions ("Terms") govern your use of our website, mobile application, and services. By accessing or using our services, you agree to be bound by these Terms and all applicable laws and regulations of Singapore. If you do not agree with any part of these terms, you may not access the service.
                    </p>
                  </section>

                  {/* Definitions */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Definitions</h2>
                    <ul className="font-inter text-slate-300 space-y-2 ml-4">
                      <li>• <strong>"Company"</strong> (referred to as "we," "our," or "us") refers to the service provider</li>
                      <li>• <strong>"Services"</strong> refers to our website, mobile app, and related technologies</li>
                      <li>• <strong>"Products"</strong> refers to food and beverage items offered through our platform</li>
                      <li>• <strong>"User"</strong> refers to any individual accessing or using our Services</li>
                      <li>• <strong>"Account"</strong> refers to your registered user profile</li>
                      <li>• <strong>"Content"</strong> refers to text, images, videos, and other materials on our platform</li>
                    </ul>
                  </section>

                  {/* Acceptance and Eligibility */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4 flex items-center space-x-3">
                      <Users className="h-6 w-6 text-green-400" />
                      <span>Acceptance and Eligibility</span>
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-poppins font-semibold text-lg text-pink-400 mb-2">Age Requirements</h3>
                        <p className="font-inter text-slate-300 leading-relaxed">
                          You must be at least 18 years old to use our Services. By using our Services, you represent and warrant that you meet this age requirement and have the legal capacity to enter into these Terms under Singapore law.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-poppins font-semibold text-lg text-pink-400 mb-2">Account Registration</h3>
                        <ul className="font-inter text-slate-300 space-y-2 ml-4">
                          <li>• You must provide accurate, current, and complete information during registration</li>
                          <li>• You are responsible for maintaining the confidentiality of your account credentials</li>
                          <li>• You must notify us immediately of any unauthorized use of your account</li>
                          <li>• One account per person is permitted</li>
                          <li>• We reserve the right to suspend or terminate accounts that violate these Terms</li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Use of Services */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4 flex items-center space-x-3">
                      <Globe className="h-6 w-6 text-blue-400" />
                      <span>Use of Services</span>
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-poppins font-semibold text-lg text-pink-400 mb-2">Permitted Use</h3>
                        <p className="font-inter text-slate-300 leading-relaxed">
                          Our Services are provided for personal, non-commercial use. You may use our platform to browse products, make purchases, and access related features as intended.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-poppins font-semibold text-lg text-pink-400 mb-2">User Responsibilities</h3>
                        <ul className="font-inter text-slate-300 space-y-2 ml-4">
                          <li>• Comply with all applicable laws and regulations</li>
                          <li>• Respect the rights and privacy of other users</li>
                          <li>• Use the Services only for lawful purposes</li>
                          <li>• Maintain the security of your account information</li>
                          <li>• Report any suspected violations of these Terms</li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Products and Orders */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4 flex items-center space-x-3">
                      <ShoppingCart className="h-6 w-6 text-purple-400" />
                      <span>Products and Orders</span>
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-poppins font-semibold text-lg text-pink-400 mb-2">Product Information</h3>
                        <p className="font-inter text-slate-300 leading-relaxed">
                          We strive to provide accurate product descriptions, ingredients, nutritional information, and pricing. However, we do not warrant that product descriptions or other content is error-free, complete, or current. All products are subject to availability.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-poppins font-semibold text-lg text-pink-400 mb-2">Pricing and Payment</h3>
                        <ul className="font-inter text-slate-300 space-y-2 ml-4">
                          <li>• All prices are displayed in Singapore Dollars (SGD) unless otherwise stated</li>
                          <li>• Prices include applicable Goods and Services Tax (GST)</li>
                          <li>• We reserve the right to change prices without prior notice</li>
                          <li>• Payment must be received and processed before order fulfillment</li>
                          <li>• We accept various payment methods as displayed during checkout</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-poppins font-semibold text-lg text-pink-400 mb-2">Order Processing</h3>
                        <p className="font-inter text-slate-300 leading-relaxed">
                          Your order constitutes an offer to purchase products. We reserve the right to accept or decline any order for any reason. Order confirmation does not guarantee acceptance. We will notify you if we cannot fulfill your order.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Shipping and Delivery */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4 flex items-center space-x-3">
                      <Clock className="h-6 w-6 text-orange-400" />
                      <span>Shipping and Delivery</span>
                    </h2>
                    <ul className="font-inter text-slate-300 space-y-2 ml-4">
                      <li>• Delivery timeframes are estimates and not guaranteed</li>
                      <li>• Risk of loss and title for products pass to you upon delivery</li>
                      <li>• We are not liable for delays caused by third-party carriers or circumstances beyond our control</li>
                      <li>• Delivery is available within Singapore unless otherwise specified</li>
                      <li>• Additional charges may apply for expedited shipping or special delivery requirements</li>
                    </ul>
                  </section>

                  {/* Returns and Refunds */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Returns and Refunds</h2>
                    <div className="bg-black/30 rounded-xl p-6">
                      <p className="font-inter text-slate-300 leading-relaxed mb-4">
                        Due to the nature of food and beverage products, returns are generally not accepted for health and safety reasons. However, we offer:
                      </p>
                      <ul className="font-inter text-slate-300 space-y-2 ml-4">
                        <li>• Full refund for damaged, defective, or incorrect products</li>
                        <li>• Replacement for products that do not match your order</li>
                        <li>• Refunds for products that arrive past their expiration date</li>
                        <li>• Satisfaction guarantee for first-time customers (terms apply)</li>
                        <li>• Refunds processed within 7-14 business days to original payment method</li>
                      </ul>
                    </div>
                  </section>

                  {/* Prohibited Uses */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4 flex items-center space-x-3">
                      <AlertTriangle className="h-6 w-6 text-red-400" />
                      <span>Prohibited Uses</span>
                    </h2>
                    <p className="font-inter text-slate-300 leading-relaxed mb-4">You agree not to use our Services to:</p>
                    <ul className="font-inter text-slate-300 space-y-2 ml-4">
                      <li>• Violate any applicable local, state, national, or international law or regulation</li>
                      <li>• Infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
                      <li>• Harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
                      <li>• Submit false or misleading information</li>
                      <li>• Upload viruses or other malicious code</li>
                      <li>• Collect or track personal information of other users</li>
                      <li>• Spam, phish, pharm, pretext, spider, crawl, or scrape</li>
                      <li>• Use our Services for any obscene or immoral purpose</li>
                      <li>• Interfere with or circumvent security features of our Services</li>
                      <li>• Resell products for commercial purposes without authorization</li>
                    </ul>
                  </section>

                  {/* Intellectual Property */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Intellectual Property Rights</h2>
                    <div className="space-y-4">
                      <p className="font-inter text-slate-300 leading-relaxed">
                        The Service and its original content, features, and functionality are and will remain the exclusive property of the Company and its licensors. The Service is protected by copyright, trademark, and other laws. Our trademarks and trade dress may not be used without our prior written consent.
                      </p>
                      <div>
                        <h3 className="font-poppins font-semibold text-lg text-pink-400 mb-2">User Content</h3>
                        <p className="font-inter text-slate-300 leading-relaxed">
                          By posting content on our Services, you grant us a non-exclusive, worldwide, royalty-free license to use, modify, and display such content in connection with our Services.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Privacy and Data Protection */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Privacy and Data Protection</h2>
                    <p className="font-inter text-slate-300 leading-relaxed">
                      Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which complies with the Personal Data Protection Act 2012 (PDPA) of Singapore. By using our Services, you consent to the collection and use of information in accordance with our Privacy Policy.
                    </p>
                  </section>

                  {/* Disclaimers */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Disclaimers</h2>
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-6">
                      <p className="font-inter text-slate-300 leading-relaxed mb-4">
                        <strong>IMPORTANT:</strong> The information on this Service is provided on an "as is" basis. To the fullest extent permitted by law, this Company:
                      </p>
                      <ul className="font-inter text-slate-300 space-y-2 ml-4">
                        <li>• Excludes all representations and warranties relating to this website and its contents</li>
                        <li>• Excludes all liability for damages arising out of or in connection with your use of this website</li>
                        <li>• Does not guarantee the accuracy, completeness, or timeliness of information</li>
                        <li>• Makes no warranties regarding the nutritional or health benefits of products</li>
                      </ul>
                    </div>
                  </section>

                  {/* Limitation of Liability */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4 flex items-center space-x-3">
                      <Shield className="h-6 w-6 text-yellow-400" />
                      <span>Limitation of Liability</span>
                    </h2>
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-6">
                      <p className="font-inter text-slate-300 leading-relaxed">
                        To the maximum extent permitted by Singapore law, in no event shall the Company, its affiliates, agents, directors, employees, suppliers, or licensors be liable for any indirect, punitive, incidental, special, consequential, or exemplary damages, including without limitation damages for loss of profits, goodwill, use, data, or other intangible losses, arising out of or relating to the use of, or inability to use, this Service. Our total liability shall not exceed the amount you paid for the specific product or service giving rise to the claim.
                      </p>
                    </div>
                  </section>

                  {/* Indemnification */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Indemnification</h2>
                    <p className="font-inter text-slate-300 leading-relaxed">
                      You agree to defend, indemnify, and hold harmless the Company and its licensee and licensors, and their employees, contractors, agents, officers and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees).
                    </p>
                  </section>

                  {/* Termination */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Termination</h2>
                    <div className="space-y-4">
                      <p className="font-inter text-slate-300 leading-relaxed">
                        We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.
                      </p>
                      <p className="font-inter text-slate-300 leading-relaxed">
                        If you wish to terminate your account, you may simply discontinue using the Service or contact us to request account deletion.
                      </p>
                    </div>
                  </section>

                  {/* Governing Law */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4 flex items-center space-x-3">
                      <Gavel className="h-6 w-6 text-purple-400" />
                      <span>Governing Law and Jurisdiction</span>
                    </h2>
                    <div className="space-y-4">
                      <p className="font-inter text-slate-300 leading-relaxed">
                        These Terms shall be interpreted and governed by the laws of Singapore, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
                      </p>
                      <p className="font-inter text-slate-300 leading-relaxed">
                        Any disputes arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the courts of Singapore.
                      </p>
                    </div>
                  </section>

                  {/* Severability */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Severability</h2>
                    <p className="font-inter text-slate-300 leading-relaxed">
                      If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect.
                    </p>
                  </section>

                  {/* Changes to Terms */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Changes to Terms</h2>
                    <div className="space-y-4">
                      <p className="font-inter text-slate-300 leading-relaxed">
                        We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect.
                      </p>
                      <p className="font-inter text-slate-300 leading-relaxed">
                        What constitutes a material change will be determined at our sole discretion. By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms.
                      </p>
                    </div>
                  </section>

                  {/* Contact and Acknowledgment */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Acknowledgment</h2>
                    <div className="bg-black/30 rounded-xl p-6">
                      <p className="font-inter text-slate-300 leading-relaxed">
                        By using our Service, you acknowledge that you have read these Terms and Conditions and agree to be bound by them. These Terms constitute the entire agreement between you and the Company regarding the use of the Service.
                      </p>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}